import React, {memo, useEffect, useRef} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import {getCSS, getCSSVariableValue} from '../../../assets/ts/_utils'
import {shallowEqual, useSelector} from 'react-redux'
import {RootState} from '../../../../setup'
import {IWorkspaceState} from '../../../../app/@reducer/WorkspaceRedux'

interface Props extends chartOptionsProps {
    className: string
}

type chartOptionsProps = {
    chartOptions: {
        series: number[]
        categoriesXAxis: string[]
    } | undefined
}

const ChartsWidget3: React.FC<Props> = memo(({className, chartOptions}) => {

    const chartRef = useRef<HTMLDivElement | null>(null)
    const {
        colors: {
            primary,
        },
    } = useSelector<RootState>(({workspace}) => workspace, shallowEqual) as IWorkspaceState


    useEffect(() => {
        if (!chartRef.current) {
            return
        }

        const height = parseInt(getCSS(chartRef.current, 'height'))

        const chart = new ApexCharts(chartRef.current, getChartOptions(height, primary, {chartOptions}))
        if (chart) {
            chart.render()
        }

        return () => {
            if (chart) {
                chart.destroy()
            }
        }
    }, [chartRef, chartOptions, primary])

    return (
        <div className={`card ${className}`}>
            {/* begin::Header */}
            <div className="card-header border-0">
                <h3 className="card-title align-items-start flex-column">
                    <span className="card-label fw-bolder fs-3 mb-1">Nombre de visites</span>
                </h3>
            </div>
            {/* end::Header */}

            {/* begin::Body */}
            <div className="card-body p-3">
                {/* begin::Chart */}
                <div ref={chartRef} id="kt_charts_widget_3_chart" style={{height: '350px'}}></div>
                {/* end::Chart */}
            </div>
            {/* end::Body */}
        </div>
    )
})

export {ChartsWidget3}

function getChartOptions(height: number, primaryColor: string, {chartOptions}: chartOptionsProps): ApexOptions {

    const labelColor = getCSSVariableValue('--bs-gray-500'),
        borderColor = getCSSVariableValue('--bs-gray-200'),
        baseColor = getCSSVariableValue('--bs-primary'),
        lightColor = getCSSVariableValue('--bs-light-primary'),
        color = primaryColor ? primaryColor : baseColor

    return {
        series: [
            {
                name: 'Visites',
                data: chartOptions?.series || [],
            },
        ],
        chart: {
            fontFamily: 'inherit',
            type: 'area',
            height: 350,
            toolbar: {
                show: false,
            },
        },
        plotOptions: {},
        legend: {
            show: false,
        },
        dataLabels: {
            enabled: false,
        },
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                inverseColors: false,
                opacityFrom: .5,
                opacityTo: .1,
                stops: [20, 100, 100, 100],
            },
        },
        stroke: {
            curve: 'smooth',
            show: true,
            width: 3,
            colors: [color],
        },
        colors: [color, lightColor],
        xaxis: {
            categories: chartOptions?.categoriesXAxis,
            axisBorder: {
                show: false,
            },
            axisTicks: {
                show: false,
            },
            labels: {
                show: true,
                rotate: -35,
                rotateAlways: false,
                hideOverlappingLabels: true,
                showDuplicates: false,
                trim: false,
                maxHeight: 120,
                style: {
                    colors: labelColor,
                    fontSize: '12px',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontWeight: 400,
                    cssClass: 'apexcharts-xaxis-label',
                },
                offsetX: 0,
                offsetY: 0,
            },
            crosshairs: {
                position: 'front',
                stroke: {
                    color: color,
                    width: 1,
                    dashArray: 3,
                },
            },
            tooltip: {
                enabled: true,
                formatter: undefined,
                offsetY: 0,
                style: {
                    fontSize: '12px',
                },
            },
            tickAmount: 10
        },
        yaxis: {
            labels: {
                style: {
                    colors: labelColor,
                    fontSize: '12px',
                },
            },
        },
        states: {
            normal: {
                filter: {
                    type: 'none',
                    value: 0,
                },
            },
            hover: {
                filter: {
                    type: 'none',
                    value: 0,
                },
            },
            active: {
                allowMultipleDataPointsSelection: false,
                filter: {
                    type: 'none',
                    value: 0,
                },
            },
        },
        tooltip: {
            style: {
                fontSize: '12px',
            },
            y: {formatter: val => val + ' visites'},
        },
        grid: {
            borderColor: borderColor,
            strokeDashArray: 4,
            yaxis: {
                lines: {
                    show: true,
                },
            },
        },
        markers: {

            strokeColors: color,
            strokeWidth: 3,
        },
    }
}